import { ApiError, ContractError, request, requestEmpty } from './client'
import type { Permission } from './permissions'

/** The signed-in person, as `GET /api/me` and `POST /api/auth/login` describe them. */
export interface User {
  readonly id: string
  readonly email: string
  /** Kept for display only. Never branch on it — see `permissions`. */
  readonly role: string
  readonly permissions: readonly Permission[]
}

export interface Credentials {
  readonly email: string
  readonly password: string
}

function field(source: Record<string, unknown>, name: string): string {
  const value = source[name]
  if (typeof value !== 'string') {
    throw new ContractError(`"${name}" is ${typeof value}, expected string`)
  }
  return value
}

/**
 * Turn an unknown body into a `User`, or fail loudly.
 *
 * The server is ours and unlikely to lie, but "unlikely" is how a `TypeError`
 * deep inside a component happens three screens later. Checking here means a
 * contract break shows up as a contract break.
 */
export function parseUser(body: unknown): User {
  if (typeof body !== 'object' || body === null) {
    throw new ContractError(`body is ${body === null ? 'null' : typeof body}`)
  }

  const source = body as Record<string, unknown>
  const rawPermissions = source['permissions']

  if (!Array.isArray(rawPermissions)) {
    throw new ContractError('"permissions" is not an array')
  }

  const permissions: Permission[] = []
  for (const entry of rawPermissions) {
    if (typeof entry !== 'string') {
      throw new ContractError('"permissions" contains a non-string')
    }
    // A permission the server knows and this build does not is kept rather than
    // dropped: it is the server's list, and a stale interface should not silently
    // narrow it.
    permissions.push(entry as Permission)
  }

  return {
    id: field(source, 'id'),
    email: field(source, 'email'),
    role: field(source, 'role'),
    permissions,
  }
}

/**
 * Who is signed in, or `null` for nobody.
 *
 * A 401 here is the ordinary answer for an anonymous visitor, not a failure, so
 * it becomes `null`. Anything else genuinely is a failure and is thrown, which
 * is what lets the shell tell "not signed in" apart from "server is down".
 */
export async function fetchMe(signal?: AbortSignal): Promise<User | null> {
  try {
    return await request('/api/me', parseUser, {
      tolerate401: true,
      ...(signal === undefined ? {} : { signal }),
    })
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

/** Sign in. The session arrives as an HttpOnly cookie the browser stores itself. */
export function signIn(credentials: Credentials): Promise<User> {
  return request('/api/auth/login', parseUser, {
    method: 'POST',
    body: credentials,
    // A wrong password is a 401 and belongs on the form, not in the global
    // signed-out path.
    tolerate401: true,
  })
}

/** Sign out. Answers 204 and clears the cookie. */
export function signOut(): Promise<void> {
  return requestEmpty('/api/auth/logout', { method: 'POST', tolerate401: true })
}
